import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppDialog, FormField, TextareaField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useTran, useFormDirty } from "@/hooks";
import { PATHS } from "@/lib/router";
import { showToast } from "@/lib/utils/toast";
import { useReportPreviewContext } from "@/features/esg/hooks/reportPreview.context";

const CreateReportModal = ({ bypassUnsavedChanges }) => {
  const { t } = useTran();
  const navigate = useNavigate();
  const {
    appliedFilters,
    createMode,
    createReport,
    createCompletedReport,
    modalReport,
    saveDraftChanges,
    isCreateModalOpen,
    closeCreateModal,
  } = useReportPreviewContext();
  const [reportName, setReportName] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [presentedTo, setPresentedTo] = useState("");
  const [includeCoverPage, setIncludeCoverPage] = useState(true);
  const [includeContentPage, setIncludeContentPage] = useState(true);
  const [isShared, setIsShared] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formValues = useMemo(
    () => ({
      reportName,
      reportDescription,
      presentedTo,
      includeCoverPage,
      includeContentPage,
      isShared,
    }),
    [
      reportName,
      reportDescription,
      presentedTo,
      includeCoverPage,
      includeContentPage,
      isShared,
    ],
  );
  const isDirty = useFormDirty(
    formValues,
    `${isCreateModalOpen}-${createMode}-${modalReport?.id || ""}`,
  );

  useEffect(() => {
    if (!isCreateModalOpen) {
      setReportName("");
      setReportDescription("");
      setPresentedTo("");
      setIncludeCoverPage(true);
      setIncludeContentPage(true);
      setIsShared(false);
      setErrors({});
      return;
    }
    if (createMode === "edit-draft" && modalReport) {
      setReportName(modalReport.name || "");
      setReportDescription(modalReport.description || "");
      setPresentedTo(modalReport.presentedTo || "");
      setIncludeCoverPage(Boolean(modalReport.includeCoverPage));
      setIncludeContentPage(Boolean(modalReport.includeContentPage));
      setIsShared(false);
      setErrors({});
    }
  }, [createMode, isCreateModalOpen, modalReport]);

  const isCompletedMode = createMode === "completed";
  const isEditDraftMode = createMode === "edit-draft";
  const dialogTitle = isCompletedMode
    ? t("Create Completed Report")
    : isEditDraftMode
      ? t("Edit Draft Report")
      : t("Save Draft");
  const dialogDescription = isCompletedMode
    ? t("Configure the finalized report before generating a fixed snapshot.")
    : isEditDraftMode
      ? t("Update the draft metadata and save the current filters.")
      : t("Save the current report configuration as a draft.");
  const submitLabel = isCompletedMode
    ? t("Create Completed Report")
    : isEditDraftMode
      ? t("Update Changes")
      : t("Save Draft");

  const handleCreate = async () => {
    const nextErrors = {};

    if (!reportName?.trim()) {
      nextErrors.reportName = t("Report name is required");
    }

    if (!presentedTo?.trim()) {
      nextErrors.presentedTo = t("Presented to is required");
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    if (!appliedFilters) {
      showToast.error(t("Please apply filters before creating a report"));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: reportName,
        description: reportDescription,
        presentedTo,
        includeCoverPage,
        includeContentPage,
        isShared,
      };

      const result = isEditDraftMode
        ? {
            report: await saveDraftChanges({
              reportId: modalReport?.id,
              ...payload,
            }),
            completed: false,
          }
        : isCompletedMode
          ? await createCompletedReport(payload)
          : { report: await createReport(payload), completed: false };
      const report = result?.report || null;

      if (!report) {
        showToast.error(
          isEditDraftMode
            ? t("Unable to save draft changes")
            : isCompletedMode
              ? t("Unable to create completed report")
              : t("Unable to save draft"),
        );
        return;
      }

      if (isCompletedMode && !result?.completed) {
        showToast.error(
          t("Draft saved, but unable to mark the report as completed."),
        );
      } else {
        showToast.success(
          isEditDraftMode
            ? t("Draft updated")
            : isCompletedMode
              ? t("Completed report created")
              : t("Draft saved"),
        );
      }
      closeCreateModal();
      bypassUnsavedChanges?.();
      if (isEditDraftMode) {
        navigate(PATHS.ESG_REPORT_PREVIEW, {
          replace: true,
          state: {
            previewReport: report,
            editMode: "draft",
          },
        });
      } else {
        navigate(PATHS.ESG_REPORT_PREVIEW_LIST, {
          state: {
            reportStatus:
              isCompletedMode && result?.completed ? "completed" : "draft",
          },
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={isCreateModalOpen}
      onOpenChange={(openState) =>
        !openState && !isSubmitting && closeCreateModal()
      }
      isDirty={isDirty}
      title={dialogTitle}
      description={dialogDescription}
      size="md"
      footer={
        <>
          <Button
            variant="outline"
            onClick={closeCreateModal}
            disabled={isSubmitting}
          >
            {t("Cancel")}
          </Button>
          <Button onClick={handleCreate} disabled={isSubmitting}>
            {isSubmitting ? t("Saving...") : submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField
          label={t("Report Name")}
          value={reportName}
          required
          onChange={(event) => {
            setReportName(event.target.value);
            if (errors.reportName) {
              setErrors((prev) => ({ ...prev, reportName: null }));
            }
          }}
          placeholder={t("ESG Report")}
          error={errors.reportName}
        />

        <TextareaField
          label={t("Description")}
          value={reportDescription}
          onChange={(event) => setReportDescription(event.target.value)}
          placeholder={t("Add a description for this report.")}
          rows={3}
          maxLength={240}
          showCount
        />

        <FormField
          label={t("Presented To")}
          value={presentedTo}
          required
          onChange={(event) => {
            setPresentedTo(event.target.value);
            if (errors.presentedTo) {
              setErrors((prev) => ({ ...prev, presentedTo: null }));
            }
          }}
          placeholder={t("Organization or recipient name")}
          error={errors.presentedTo}
        />

        {isCompletedMode && (
          <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50/40 px-3 py-2.5 dark:border-amber-700 dark:bg-amber-950/20">
            <Checkbox
              id="isShared-preview"
              checked={isShared}
              onCheckedChange={setIsShared}
              className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
            />
            <div className="space-y-0.5">
              <label
                htmlFor="isShared-preview"
                className="text-sm font-medium text-amber-800 dark:text-amber-300 cursor-pointer"
              >
                {t("Share this report")}
              </label>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {t(
                  "When enabled, this report will be visible to other users in your organization.",
                )}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-4 rounded-radius border border-border bg-muted/30 px-3 py-2">
          <div className="space-y-1">
            <Label className="text-sm font-medium">
              {t("Include cover page")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("Adds a title page to the exported preview.")}
            </p>
          </div>
          <Switch
            checked={includeCoverPage}
            onCheckedChange={setIncludeCoverPage}
          />
        </div>

        <div className="flex items-center justify-between gap-4 rounded-radius border border-border bg-muted/30 px-3 py-2">
          <div className="space-y-1">
            <Label className="text-sm font-medium">
              {t("Include content page")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("Adds a contents overview page to the preview output.")}
            </p>
          </div>
          <Switch
            checked={includeContentPage}
            onCheckedChange={setIncludeContentPage}
          />
        </div>

        {/* TODO: add additional fields (format, sections, brand theme, etc.) */}
      </div>
    </AppDialog>
  );
};

export default CreateReportModal;
